import { useEffect, useState } from "react";
import {
  Typography, Box, Card, Dialog, DialogTitle, DialogContent, DialogActions,
  Container, Stack, Divider, IconButton, Paper, Button,
  Avatar, Drawer, CircularProgress, Pagination
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GlobalSkeleton from "../components/GlobalSkeleton";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import CancelIcon from '@mui/icons-material/Cancel';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import API from "../api/api";
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Rating } from "@mui/material";
import { getCachedData, setCachedData } from "../utils/cache";

export default function OrderPage() {
  const CACHE_KEY = "mealno_orders";
  const [page, setPage] = useState(1);
  const ordersPerPage = 5;

  const [orders, setOrders] = useState(() => {
    const cached = getCachedData(
      CACHE_KEY,
      999999
    );

    if (!cached) return [];

    return [...cached].sort(
      (a, b) =>
        new Date(b.created_at).getTime() -
        new Date(a.created_at).getTime()
    );
  });

  const [loading, setLoading] = useState(() => {
    return !getCachedData(
      CACHE_KEY,
      999999
    );
  });
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [dateFilter, setDateFilter] = useState("ALL");
  const navigate = useNavigate();
  const [customDate, setCustomDate] = useState("");
  const [openDateDialog, setOpenDateDialog] = useState(false);

  const loadOrders = async () => {
    const userData = localStorage.getItem("user");
    if (!userData) { setLoading(false); return; }

    const user = JSON.parse(userData);
    try {
      const res = await API.get(`/user/${user.id}/orders?skip=0&limit=100`);

      if (res.data && res.data.success === false) {
        console.error("Load failed", res.data.error);
        return;
      }

      const fetchedOrders = Array.isArray(res.data)
        ? [...res.data].sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        )
        : [];

      setOrders(fetchedOrders);
      setCachedData(
        CACHE_KEY,
        fetchedOrders
      );
    } catch (err) {
      console.error("Load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrders(); }, []);

  const getStatusStyle = (status) => {
    const s = status?.toUpperCase() || "";
    if (s === 'DELIVERED') return { color: '#6A9E3A', bgcolor: '#DDECC8', icon: <CheckCircleIcon /> };
    if (s === 'CANCELLED' || s === 'REJECTED') return { color: '#E85D3F', bgcolor: '#FFF0EE', icon: <CancelIcon /> };

    return { color: '#F29D17', bgcolor: '#FFF6DE', icon: <AccessTimeFilledIcon /> };
  };

  const [reviewDialog, setReviewDialog] = useState(false);
  const [orderToReview, setOrderToReview] = useState(null);
  const [reviewStep, setReviewStep] = useState(1);
  const [ratingValue, setRatingValue] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const submitReview = async (skipText = false) => {
    if (!orderToReview || ratingValue === 0) return;
    setSubmittingReview(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      await API.post("/order/review", {
        user_id: user.id,
        order_id: orderToReview.order_id,
        rating: ratingValue,
        comment: skipText ? "" : reviewText
      });
      setReviewDialog(false);
      loadOrders();
    } catch (err) { console.error(err); }
    finally {
      setSubmittingReview(false);
      setOrderToReview(null); setRatingValue(0); setReviewText(""); setReviewStep(1);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (dateFilter === "ALL") return true;

    const orderDate = new Date(order.created_at);
    const now = new Date();

    if (dateFilter === "TODAY") {
      return (
        orderDate.toDateString() === now.toDateString()
      );
    }

    if (dateFilter === "WEEK") {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      return orderDate >= weekAgo;
    }

    if (dateFilter === "MONTH") {
      return (
        orderDate.getMonth() === now.getMonth() &&
        orderDate.getFullYear() === now.getFullYear()
      );
    }

    if (dateFilter === "CUSTOM") {
      if (!customDate) return true;
      const selectedDate = new Date(customDate);
      return (
        orderDate.toDateString() ===
        selectedDate.toDateString()
      );
    }

    return true;
  });

  const handleReorder = (e, orderItems) => {
    e.stopPropagation();

    const formattedItems = orderItems.map(item => ({
      id: item.menu_item_id,
      name: item.name,
      selling_price: item.price,
      quantity: item.quantity,
      vendor_id: item.vendor_id,
      image: item.image || null,
      category: item.category
    }));

    localStorage.setItem("mealnoCart", JSON.stringify(formattedItems));
    window.dispatchEvent(new Event("storage"));
    navigate("/cart");
  };

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const isAnyModalOpen = !!selected || openDateDialog;

    if (isAnyModalOpen) {
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    } else {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "auto";
    }

    window.dispatchEvent(new Event("modal-state-change"));

    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "auto";
      window.dispatchEvent(new Event("modal-state-change"));
    };
  }, [selected, openDateDialog]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pb: 10 }}>
        <Box sx={{
          background: "#F8D548",
          pt: 2, pb: 2, px: 2,
          minHeight: "75px",
          borderRadius: "0 0 24px 24px",
          borderBottom: '2px solid #c3a169c4'
        }} />

        <Box sx={{ mt: 3 }}>
          <GlobalSkeleton type="list" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pb: 14 }}>
      {/* Header */}
      <Box sx={{
        background: "#F8D548",
        pt: -5, pb: 1,
        borderRadius: "0 0 24px 24px",
        color: '#3A2C23',
        borderBottom: '2px solid #c3a169c4'
      }}>
        <Container maxWidth="md">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" fontWeight="900">
                My Orders
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              <Stack alignItems="center" spacing={0.5}>
                <IconButton
                  onClick={() => {
                    setDateFilter("ALL");
                    setCustomDate("");
                    setPage(1);
                    loadOrders();
                  }}
                  sx={{
                    bgcolor: '#FFFFFF',
                    color: '#5C4E42',
                    border: '1px solid #ECE5DA',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s ease',
                    width: 48, height: 48,
                    '&:hover': {
                      bgcolor: '#fff',
                      transform: 'rotate(180deg)'
                    },
                    '&:active': {
                      transform: 'rotate(180deg) scale(0.90)'
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#5C4E42' }}>
                  Refresh
                </Typography>
              </Stack>

              <Stack alignItems="center" spacing={0.5}>
                <IconButton
                  onClick={(e) => setFilterAnchor(e.currentTarget)}
                  sx={{
                    bgcolor: '#FFFFFF',
                    color: '#5C4E42',
                    border: '1px solid #ECE5DA',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s ease',
                    width: 48, height: 48,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.5)'
                    },
                    '&:active': {
                      transform: 'scale(0.90)'
                    }
                  }}
                >
                  <CalendarMonthIcon />
                </IconButton>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#5C4E42' }}>
                  Calendar
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mt: 1
          }
        }}
      >
        <MenuItem onClick={() => {
          setDateFilter("ALL");
          setFilterAnchor(null);
        }}>
          All Orders
        </MenuItem>
        <MenuItem onClick={() => {
          setDateFilter("TODAY");
          setFilterAnchor(null);
        }}>
          Today
        </MenuItem>
        <MenuItem onClick={() => {
          setDateFilter("WEEK");
          setFilterAnchor(null);
        }}>
          Last 7 Days
        </MenuItem>
        <MenuItem onClick={() => {
          setDateFilter("MONTH");
          setFilterAnchor(null);
        }}>
          This Month
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterAnchor(null);
            setOpenDateDialog(true);
          }}
        >
          <DateRangeIcon sx={{ fontSize: 18, mr: 1, color: '#FF4B2B' }} />
          Choose Date
        </MenuItem>
      </Menu>

      <Drawer
        anchor="bottom"
        open={openDateDialog}
        onClose={() => setOpenDateDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "28px 28px 0 0", overflow: "visible", bgcolor: "#fff", height: "auto", maxHeight: "78vh"
          }
        }}
      >
        <IconButton
          onClick={() => setOpenDateDialog(false)}
          sx={{
            position: "absolute", top: -55, left: "50%", transform: "translateX(-50%)",
            bgcolor: "rgba(0,0,0,0.6)", color: "#fff", width: 42, height: 42, zIndex: 1300,
            border: "1px solid rgba(255,255,255,0.25)", backdropFilter: "blur(10px)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.8)" }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ background: "#FFF6DE", color: "#3A2C23", borderBottom: "1px solid #ECE5DA", pt: 2, pb: 2.2, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="900">
            Select Order Date
          </Typography>
        </Box>

        <Box sx={{ p: 2, overflowY: "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={customDate ? dayjs(customDate) : null}
              onChange={(newValue) => {
                if (!newValue) return;
                const formatted = dayjs(newValue).format("YYYY-MM-DD");
                setCustomDate(formatted);
                setDateFilter("CUSTOM");
              }}
              sx={{
                width: "100%",
                "& .MuiPickersDay-root": { width: 36, height: 36, fontWeight: "700", borderRadius: "12px" },
                "& .Mui-selected": { background: "#F8C332 !important", color: "#3A2C23 !important" },
                "& .MuiPickersCalendarHeader-label": { fontWeight: "900" }
              }}
            />
          </LocalizationProvider>

          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <Button
              fullWidth variant="outlined"
              onClick={() => {
                setDateFilter("ALL");
                setCustomDate("");
                setOpenDateDialog(false);
              }}
              sx={{ borderRadius: "14px", py: 1.2, textTransform: "none", fontWeight: "800", borderColor: "#ddd", color: "#666" }}
            >
              Clear
            </Button>
            <Button
              fullWidth variant="contained" disableElevation
              onClick={() => {
                setOpenDateDialog(false);
                setPage(1);
              }}
              sx={{ borderRadius: "14px", py: 1.2, fontWeight: "900", textTransform: "none", bgcolor: "#F8C332", color: "#3A2C23" }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Container maxWidth="md" sx={{ mt: 2 }}>
        {orders.length === 0 ? (
          <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '30px', boxShadow: '0 10px 40px rgba(0,0,0,0.05)' }}>
            <ShoppingBagIcon sx={{ fontSize: 80, color: '#eee', mb: 2 }} />
            <Typography variant="h6" fontWeight="800" color="text.secondary">No orders yet</Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/menu")}
              sx={{ mt: 3, bgcolor: '#6D4C41', borderRadius: '12px', px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              Explore Menu
            </Button>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {filteredOrders
              .slice(
                (page - 1) * ordersPerPage,
                page * ordersPerPage
              )
              .map((o) => {
                return (
                  <Card
                    key={o.order_id}
                    onClick={() => setSelected(o)}
                    sx={{
                      borderRadius: '20px',
                      mb: 2,
                      bgcolor: '#FFFFFF',
                      border: 'none',
                      boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer', // Added cursor pointer
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ mb: 1.5 }} >
                        <Typography variant="caption" sx={{ fontWeight: '700', color: 'text.secondary' }}>
                          Order ID : #{o.order_id.toString().slice(-5)}
                        </Typography>
                      </Box>

                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={1.5}>
                          <Avatar
                            variant="rounded"
                            sx={{
                              bgcolor: getStatusStyle(o.status).bgcolor,
                              color: getStatusStyle(o.status).color,
                              width: 40, height: 40
                            }}
                          >
                            {getStatusStyle(o.status).icon}
                          </Avatar>

                          <Box>
                            <Typography variant="subtitle1" fontWeight="900" sx={{ color: getStatusStyle(o.status).color, lineHeight: 1.2 }}>
                              {o.status === 'PLACED' && "Order Received"}
                              {o.status === 'ACCEPTED' && "Preparing your food"}
                              {o.status === 'READY' && "Food is ready for pickup"}
                              {o.status === 'ASSIGNED' && "Delivery partner assigned"}
                              {(o.status === 'PICKED_UP' || o.status === 'OUT_FOR_DELIVERY') && "Food is on the way"}
                              {o.status === 'DELIVERED' && "Order delivered"}
                              {o.status === 'CANCELLED' && "Order Cancelled"}
                              {o.status === 'REJECTED' && "Order Cancelled"}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ display: 'block', maxWidth: '180px', mt: 0.5 }}
                            >
                              {o.address_flat ? (
                                [o.address_flat, o.address_building, o.address_landmark].filter(Boolean).join(", ")
                              ) : "No address provided"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                      <Stack spacing={0.5}>
                        {o.items.map((item, idx) => (
                          <Stack key={idx} direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 10, height: 10, border: '1px solid', borderColor: item.category?.toLowerCase().includes('non') ? '#e44444' : '#00b050', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: item.category?.toLowerCase().includes('non') ? '#e44444' : '#00b050' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">{item.quantity} x {item.name}</Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Placed on {new Date(o.created_at).toLocaleDateString()}</Typography>
                          <Typography variant="body2" fontWeight="900">₹{o.final_amount}</Typography>

                          {o.status === "DELIVERED" && !o.is_reviewed && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOrderToReview(o);
                                setReviewStep(1);
                                setReviewDialog(true);
                              }}>
                              <Rating value={0} readOnly size="small" />
                              <Typography variant="caption" color="primary" fontWeight="bold">Rate Order</Typography>
                            </Box>
                          )}
                        </Box>

                        {o.status === "DELIVERED" && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleReorder(e, o.items);
                            }}
                            sx={{ color: '#F29D17', textTransform: 'uppercase', fontWeight: 'bold' }}
                          >
                            Reorder
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Card>
                );
              })}

            {filteredOrders.length > ordersPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4 }}>
                <Pagination
                  count={Math.ceil(filteredOrders.length / ordersPerPage)}
                  page={page}
                  onChange={(e, value) => {
                    setPage(value);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{
                    '& .Mui-selected': { bgcolor: '#F8C332 !important', color: '#3A2C23', fontWeight: 'bold' },
                    '& .MuiPaginationItem-root': { color: '#6E655E', fontWeight: '600' }
                  }}
                />
              </Box>
            )}
          </Stack>
        )}
      </Container>

      {/* ORDER DETAILS DRAWER (Bottom Sheet) */}
      <Drawer
        anchor="bottom"
        open={!!selected}
        onClose={() => setSelected(null)}
        PaperProps={{
          sx: {
            borderRadius: "24px 24px 0 0",
            overflow: "visible",
            height: "75vh",
            maxHeight: "75vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#FFFFE0"
          },
        }}
      >
        {selected && (
          <>
            <IconButton
              onClick={() => setSelected(null)}
              sx={{
                position: "absolute", top: -55, left: "50%", transform: "translateX(-50%)",
                bgcolor: "rgba(0,0,0,0.6)", color: "#fff", zIndex: 1300, border: "1px solid rgba(255,255,255,0.3)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>

              <Typography variant="h6" sx={{ fontWeight: '900', textAlign: 'center', pb: 2, pt: 1 }}>
                Order Details
              </Typography>

              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FEF2CE', borderRadius: '15px', mb: 2, border: '2px solid #eee' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="900">
                    {JSON.parse(localStorage.getItem("user"))?.name || "User"}
                  </Typography>
                  <Typography variant="caption" color="#756B63">
                    {selected?.address_flat ? (
                      [selected.address_flat, selected.address_building, selected.address_landmark].filter(Boolean).join(", ")
                    ) : "No address linked to this order"}
                  </Typography>
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="900" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingBagIcon fontSize="small" /> Bill Summary
                </Typography>

                {selected?.status === "DELIVERED" && (
                  <IconButton
                    size="small"
                    disabled={downloadingInvoice}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        setDownloadingInvoice(true);
                        const res = await API.get(`/invoice/${selected.order_id}/pdf`, { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", `invoice_${selected.order_id}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error("Invoice download failed", err);
                      } finally {
                        setDownloadingInvoice(false);
                      }
                    }}
                    sx={{
                      color: '#F29D17', bgcolor: '#FFF6DE', transition: "all 0.25s ease",
                      '&:hover': { bgcolor: '#F8C332', transform: "scale(1.05)" },
                      '&.Mui-disabled': { bgcolor: '#FFF6DE', opacity: 0.8 }
                    }}
                  >
                    {downloadingInvoice ? <CircularProgress size={18} thickness={5} sx={{ color: '#F8C332' }} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                )}
              </Box>

              <Stack spacing={1.5} sx={{ px: 1 }}>
                {selected?.items.map((item, idx) => (
                  <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 10, height: 10, border: '1px solid',
                          borderColor: item.category?.toLowerCase().startsWith('n') ? '#e44444' : '#00b050',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}
                      >
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: item.category?.toLowerCase().startsWith('n') ? '#e44444' : '#00b050' }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{item.quantity} x {item.name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {item.display_price > item.price && (
                        <Typography sx={{ textDecoration: "line-through", color: "#999", fontSize: 12, fontWeight: 600 }}>
                          ₹{item.display_price * item.quantity}
                        </Typography>
                      )}
                      <Typography variant="body2" fontWeight="700" sx={{ color: "#FF4B2B" }}>
                        ₹{item.price * item.quantity}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}

                <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

                {/* 👇 CONDITIONAL CANCELLATION BOX vs BILLING TOTALS 👇 */}
                {selected.status === "CANCELLED" || selected.status === "REJECTED" ? (
                  <Box sx={{ p: 2, mt: 1, bgcolor: "#FFF0EE", borderRadius: "12px", border: "1px dashed #F5D0C9", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: '#E85D3F', fontWeight: '900', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      ORDER CANCELLED - "{selected.cancel_reason || 'Order was cancelled.'}"
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Item Total</Typography>
                      <Typography variant="body2" fontWeight="700">₹{selected.item_total}</Typography>
                    </Stack>

                    {selected.additional_discount > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: "#16A34A" }}>Additional Discount</Typography>
                        <Typography variant="body2" fontWeight="700" sx={{ color: "#16A34A" }}>-₹{selected.additional_discount}</Typography>
                      </Stack>
                    )}

                    {selected.coupon_discount > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: "#16A34A" }}>Coupon Discount</Typography>
                        <Typography variant="body2" fontWeight="700" sx={{ color: "#16A34A" }}>-₹{selected.coupon_discount}</Typography>
                      </Stack>
                    )}

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Delivery Charge</Typography>
                      <Typography variant="body2" fontWeight="700" sx={{ color: "#16A34A" }}>FREE</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Platform Fee</Typography>
                      <Typography variant="body2" fontWeight="700" sx={{ color: "#16A34A" }}>FREE</Typography>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle1" fontWeight="900">Total Bill</Typography>
                      <Typography variant="subtitle1" fontWeight="900" color="#2F2A25">₹{selected.final_amount}</Typography>
                    </Stack>
                  </>
                )}
                {/* 👆 END CONDITIONAL BLOCK 👆 */}

              </Stack>

              {/* 4. PAYMENT & DATE INFO BLOCK */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
                <Stack spacing={2}>
                  {selected?.status === "DELIVERED" && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: '900', letterSpacing: 0.5 }}>
                        Payment Method
                      </Typography>
                      <Typography variant="body2" fontWeight="700" sx={{ color: '#1a1a1a' }}>
                        Paid via :
                        <Typography component="span" fontWeight="700" sx={{ ml: 0.5, color: selected?.payment_method?.toUpperCase() === "COD" ? "#D97706" : "#3BB78F" }}>
                          {selected?.payment_method || "UPI"}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: '700', letterSpacing: 0.5 }}>
                      Order Date
                    </Typography>
                    <Typography variant="body2" fontWeight="700" sx={{ color: '#1a1a1a' }}>
                      {selected?.created_at ? (
                        (() => {
                          const dateStr = selected.created_at.endsWith('Z') ? selected.created_at : `${selected.created_at}Z`;
                          const dateObj = new Date(dateStr);
                          return dateObj.toLocaleString('en-IN', {
                            timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', hour12: true
                          }).toUpperCase();
                        })()
                      ) : "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </>
        )}
      </Drawer>

      <Dialog
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        PaperProps={{ sx: { borderRadius: '20px', p: 2, minWidth: '320px', textAlign: 'center' } }}
      >
        <DialogTitle sx={{ fontWeight: '900' }}>
          {reviewStep === 1 ? "How was your food?" : "Tell us more!"}
        </DialogTitle>
        <DialogContent sx={{ pb: 2, textAlign: 'center' }}>
          {reviewStep === 1 ? (
            <Box sx={{ py: 2 }}>
              <Rating
                size="large" value={ratingValue}
                onChange={(e, val) => {
                  setRatingValue(val);
                  if (val > 0) setReviewStep(2);
                }}
                sx={{ fontSize: '3rem', color: '#F8C332' }}
              />
            </Box>
          ) : (
            <Box sx={{ py: 1 }}>
              <TextField
                multiline rows={3} fullWidth autoFocus
                placeholder="Share your experience..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F8F9FA' } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1 }}>
          {reviewStep === 1 ? (
            <Button
              fullWidth variant="outlined"
              onClick={() => { setReviewDialog(false); setRatingValue(0); setReviewStep(1); }}
              sx={{ borderRadius: '10px', color: '#6E655E', borderColor: '#ECE5DA', fontWeight: 'bold', textTransform: 'none' }}
            >
              Not Now
            </Button>
          ) : (
            <>
              <Button fullWidth variant="outlined" onClick={() => submitReview(true)} sx={{ borderRadius: '10px', color: '#6E655E', borderColor: '#ECE5DA' }}>Skip</Button>
              <Button fullWidth variant="contained" disableElevation disabled={!reviewText.trim()} onClick={() => submitReview(false)} sx={{ borderRadius: '10px', bgcolor: '#F8C332', color: '#3A2C23' }}>
                {submittingReview ? <CircularProgress size={20} sx={{ color: '#3A2C23' }} /> : "Submit"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}